import React, { Dispatch } from 'react';
import { AssessmentTestResponse } from '../../api/teams';
import {
  UpdateStepActiveSectionActionType,
  updateStepActiveSectionReducer,
} from './reducers/update-step-section';
import {
  isFillInTheGapQuestionAnswered,
  isMatchingQuestionAnswered,
  isPythonPlaygroundQuestionAnswered,
  isMultipleChoiceQuestionAnswered,
  isMultipleRatingQuestionAnswered,
  isRankingQuestionAnswered,
  isTextInputQuestionAnswered,
  sectionBuilders,
  updateSectionOfType,
  isLLMPlaygroundQuestionAnswered,
  isSectionAnswered,
  isPythonEditorQuestionAnswered,
  isLLMEditorQuestionAnswered,
  isTextTemplateQuestionAnswered,
  isMatrixRankingQuestionAnswered,
} from './helpers';
import {
  GoToNextStepActionType,
  goToNextStepReducer,
} from './reducers/go-to-next-step';
import {
  goToPreviousStepReducer,
  GoToPreviousStepActionType,
} from './reducers/go-to-previous-step';
import {
  FinishTestActionType,
  finishTestReducer,
} from './reducers/finish-test';
import {
  TimeoutTestActionType,
  timeoutTestReducer,
} from './reducers/timeout-test';
import { StartTestActionType, startTestReducer } from './reducers/start-test';

export type TimeInterval = {
  startedAt: Date;
  endedAt: Date | null;
};

export enum SyncStatus {
  Idle = 'idle',
  Queued = 'queued',
  Syncing = 'syncing',
  QueuedWhileSyncing = 'queued-while-syncing',
}

export type AssessmentTestContextType = {
  testId: number;
  testSessionId: number;
  companyId: number;

  screen:
    | {
        type: 'INTRO';
      }
    | {
        type: 'STEP';
        stepId: string;
        sectionId: string | null;
        stepIndex: number;
      }
    | {
        type: 'OUTRO';
      }
    | {
        type: 'TIMEOUT';
      };

  startedAt: Date | null;
  finishedAt: Date | null;
  finishReason: 'COMPLETED' | 'TIME_EXPIRED' | null;

  sync: {
    status: SyncStatus;
    lastSyncedAt: Date | null;
    error: string | null;
    backoffAttempt: number;
  };

  steps: Array<{
    id: string;
    title: string;
    label: string;
    introduction: {
      text: string;
      timeIntervals: Array<TimeInterval>;
    } | null;
    sections: Array<
      {
        id: string;
        title: string;
        didInteract: boolean;
        isAnswered: boolean;
        content: string;
        timeIntervals: Array<TimeInterval>;
      } & (
        | { type: 'text-input'; answer: string; placeholder: string | null }
        | {
            type: 'ranking';
            options: Array<{ id: string; content: string }>;
            answer: Array<{ id: string; content: string }>;
          }
        | {
            type: 'multiple-choice';
            isSingleChoice: boolean;
            options: Array<{ id: string; content: string }>;
            answer: Array<{ id: string; content: string }>;
          }
        | {
            type: 'matching';
            leftItems: Array<{ id: string; content: string }>;
            rightItems: Array<{ id: string; content: string }>;
            answer: Array<{ leftId: string; rightId: string }>;
          }
        | {
            type: 'fill-in-the-gap';
            blanks: Array<{ id: string; content: string; placeholder: string }>;
            answer: Array<{ id: string; content: string }>;
          }
        | {
            type: 'text-template';
            blanks: Array<{ id: string; content: string; placeholder: string }>;
            answer: Array<{ id: string; content: string }>;
            template: string;
          }
        | {
            type: 'py-playground';
            setupCode: string;
            startingCode: string;
            answer: {
              output: string;
              code: string;
            };
          }
        | {
            type: 'llm-playground';
            initialPrompt: string;
            answer: {
              prompt: string;
              output: string;
            };
          }
        | {
            type: 'multiple-rating';
            questions: Array<{
              id: string;
              content: string;
              options: Array<string>;
            }>;
            answer: Array<{ id: string; label: string }>;
          }
        | {
            type: 'py-editor';
            startingCode: string;
            answer: string;
          }
        | {
            type: 'llm-editor';
            initialPrompt: string;
            answer: string;
          }
        | {
            type: 'matrix-ranking';
            items: Array<{ id: string; content: string }>;
            horizontalAxisLabel: string;
            verticalAxisLabel: string;
            annotationPrompt: string;
            answer: Array<{
              item: string;
              x: number;
              y: number;
              note: string;
            }>;
          }
      ) &
        (
          | {
              hasFollowUp: true;
              followUpContent: string;
              followUpAnswer: string;
              followUpPlaceholder: string | null;
            }
          | {
              hasFollowUp: false;
              followUpContent: null;
              followUpAnswer: null;
              followUpPlaceholder: null;
            }
        )
    >;
  }>;
};

type AssessmentTestActionType =
  | StartTestActionType
  | FinishTestActionType
  | TimeoutTestActionType
  | GoToNextStepActionType
  | GoToPreviousStepActionType
  | UpdateStepActiveSectionActionType
  | {
      type: 'SYNC_START';
    }
  | {
      type: 'SYNC_SUCCESS';
      timestamp: Date;
    }
  | {
      type: 'SYNC_ERROR';
      error: string;
    }
  | {
      type: 'CHANGE_ANSWER_TEXT_INPUT';
      payload: {
        stepId: string;
        sectionId: string;
        answer: string;
      };
    }
  | {
      type: 'CHANGE_ANSWER_RANKING';
      payload: {
        stepId: string;
        sectionId: string;
        answer: Array<{ id: string; content: string }>;
      };
    }
  | {
      type: 'CHANGE_ANSWER_MULTIPLE_CHOICE';
      payload: {
        stepId: string;
        sectionId: string;
        answer: Array<{ id: string; content: string }>;
      };
    }
  | {
      type: 'CHANGE_ANSWER_MATCHING';
      payload: {
        stepId: string;
        sectionId: string;
        answer: Array<{ leftId: string; rightId: string }>;
      };
    }
  | {
      type: 'CHANGE_ANSWER_FILL_IN_THE_GAP';
      payload: {
        stepId: string;
        sectionId: string;
        answer: Array<{ id: string; content: string }>;
      };
    }
  | {
      type: 'CHANGE_ANSWER_TEXT_TEMPLATE';
      payload: {
        stepId: string;
        sectionId: string;
        answer: Array<{ id: string; content: string }>;
      };
    }
  | {
      type: 'CHANGE_ANSWER_PY_PLAYGROUND';
      payload: {
        stepId: string;
        sectionId: string;
        answer: { output: string; code: string };
      };
    }
  | {
      type: 'CHANGE_ANSWER_PY_EDITOR';
      payload: {
        stepId: string;
        sectionId: string;
        answer: string;
      };
    }
  | {
      type: 'CHANGE_ANSWER_LLM_EDITOR';
      payload: {
        stepId: string;
        sectionId: string;
        answer: string;
      };
    }
  | {
      type: 'CHANGE_ANSWER_MULTIPLE_RATING';
      payload: {
        stepId: string;
        sectionId: string;
        answer: Array<{ id: string; label: string }>;
      };
    }
  | {
      type: 'CHANGE_ANSWER_LLM_PLAYGROUND';
      payload: {
        stepId: string;
        sectionId: string;
        answer: { prompt: string; output: string };
      };
    }
  | {
      type: 'CHANGE_FOLLOW_UP_ANSWER';
      payload: {
        stepId: string;
        sectionId: string;
        followUpAnswer: string;
      };
    }
  | {
      type: 'CHANGE_ANSWER_MATRIX_RANKING';
      payload: {
        stepId: string;
        sectionId: string;
        answer: Array<{
          item: string;
          x: number;
          y: number;
          note: string;
        }>;
      };
    };

const DEFAULT_STATE: AssessmentTestContextType = {
  testId: -1,
  testSessionId: -1,
  companyId: -1,
  screen: { type: 'INTRO' },
  startedAt: null,
  finishedAt: null,
  finishReason: null,
  sync: {
    status: SyncStatus.Idle,
    lastSyncedAt: null,
    error: null,
    backoffAttempt: 0,
  },
  steps: [],
};

export const AssessmentTestContext = React.createContext<
  [AssessmentTestContextType, Dispatch<AssessmentTestActionType>]
>([DEFAULT_STATE, () => {}]);

export function AssessmentTestProvider({
  test,
  children,
}: {
  test: AssessmentTestResponse;
  children: React.ReactNode;
}) {
  const [state, dispatch] = React.useReducer(
    assessmentTestReducer,
    mapAssessmentTestResponseToState({ test })
  );

  return (
    <AssessmentTestContext.Provider value={[state, dispatch]}>
      {children}
    </AssessmentTestContext.Provider>
  );
}

function mapAssessmentTestResponseToState({
  test,
}: {
  test: AssessmentTestResponse;
}): AssessmentTestContextType {
  let screen: AssessmentTestContextType['screen'];
  if (!test.startedAt) {
    screen = { type: 'INTRO' };
  } else if (test.finishedAt) {
    screen = { type: 'OUTRO' };
  } else {
    const firstUnansweredIndex = test.steps.findIndex((s) => {
      const stepAnswers = test.answers.find((a) => a.id === s.id);
      if (!stepAnswers) {
        return true;
      }
      return stepAnswers.sections.some((a) => !a.didInteract && !a.isAnswered);
    });
    const index =
      firstUnansweredIndex === -1
        ? test.steps.length - 1
        : firstUnansweredIndex;
    const currentStep = test.steps[index];
    screen = {
      type: 'STEP',
      stepId: currentStep.id,
      sectionId: null,
      stepIndex: index,
    };
  }

  return {
    testId: test.assessmentTestId,
    testSessionId: test.assessmentTestSessionId,
    companyId: test.companyId,
    screen,
    startedAt: test.startedAt ?? null,
    finishedAt: test.finishedAt ?? null,
    finishReason: test.finishReason ?? null,
    sync: {
      status: SyncStatus.Idle,
      lastSyncedAt: null,
      error: null,
      backoffAttempt: 0,
    },
    steps: test.steps.map((step) => {
      const stepAnswer = test.answers.find((a) => a.id === step.id);
      return {
        id: step.id,
        title: step.title,
        label: step.label,
        introduction: step.introduction
          ? {
              text: step.introduction ?? '',
              timeIntervals: stepAnswer?.introduction?.timeIntervals ?? [],
            }
          : null,
        sections: step.sections.map((section, index) => {
          const sectionAnswer = stepAnswer?.sections.find(
            (a) => a.id === section.id && section.type === a.type
          );
          const questionSpecificField = sectionBuilders[section.type](
            section as never,
            sectionAnswer as never
          );

          const followUpFields = section?.followUpContent
            ? {
                hasFollowUp: true as const,
                followUpContent: section.followUpContent ?? '',
                followUpAnswer: sectionAnswer?.followUpAnswer ?? '',
                followUpPlaceholder: section.followUpPlaceholder ?? null,
              }
            : {
                hasFollowUp: false as const,
                followUpContent: null,
                followUpAnswer: null,
                followUpPlaceholder: null,
              };

          return {
            id: section.id,
            title: `Question ${index + 1}`,
            didInteract: sectionAnswer?.didInteract ?? false,
            isAnswered: sectionAnswer?.isAnswered ?? false,
            content: section.content,
            timeIntervals: sectionAnswer?.timeIntervals ?? [],
            ...followUpFields,
            ...questionSpecificField,
          };
        }),
      };
    }),
  };
}

function assessmentTestReducer(
  state: AssessmentTestContextType,
  action: AssessmentTestActionType
): AssessmentTestContextType {
  switch (action.type) {
    case 'GO_TO_NEXT_STEP': {
      return goToNextStepReducer(state, action);
    }
    case 'GO_TO_PREVIOUS_STEP': {
      return goToPreviousStepReducer(state, action);
    }
    case 'UPDATE_STEP_ACTIVE_SECTION': {
      return updateStepActiveSectionReducer(state, action);
    }
    case 'FINISH_TEST': {
      return finishTestReducer(state, action);
    }
    case 'TIMEOUT_TEST': {
      return timeoutTestReducer(state, action);
    }
    case 'START_TEST': {
      return startTestReducer(state, action);
    }
    case 'SYNC_START': {
      return {
        ...state,
        sync: {
          ...state.sync,
          status: SyncStatus.Syncing,
          // keep error while retrying so UI can show failed icon
          error: state.sync.error,
        },
      };
    }
    case 'SYNC_SUCCESS': {
      return {
        ...state,
        sync: {
          ...state.sync,
          status:
            state.sync.status === SyncStatus.QueuedWhileSyncing
              ? SyncStatus.Queued
              : SyncStatus.Idle,
          lastSyncedAt: action.timestamp,
          error: null,
          backoffAttempt: 0,
        },
      };
    }
    case 'SYNC_ERROR': {
      return {
        ...state,
        sync: {
          ...state.sync,
          status: SyncStatus.Queued, // Retry the sync
          error: action.error,
          backoffAttempt: (state.sync.backoffAttempt ?? 0) + 1,
        },
      };
    }

    case 'CHANGE_ANSWER_TEXT_INPUT': {
      const { stepId, sectionId, answer } = action.payload;
      return updateSectionOfType(
        state,
        stepId,
        sectionId,
        'text-input',
        (s) => ({
          ...s,
          didInteract: true,
          isAnswered: isTextInputQuestionAnswered({
            ...s,
            answer,
          }),
          answer,
        })
      );
    }
    case 'CHANGE_ANSWER_RANKING': {
      const { stepId, sectionId, answer } = action.payload;
      return updateSectionOfType(state, stepId, sectionId, 'ranking', (s) => ({
        ...s,
        didInteract: true,
        isAnswered: isRankingQuestionAnswered({
          ...s,
          answer,
        }),
        answer,
      }));
    }
    case 'CHANGE_ANSWER_MULTIPLE_RATING': {
      const { stepId, sectionId, answer } = action.payload;
      return updateSectionOfType(
        state,
        stepId,
        sectionId,
        'multiple-rating',
        (s) => ({
          ...s,
          didInteract: true,
          isAnswered: isMultipleRatingQuestionAnswered({
            ...s,
            answer,
          }),
          answer,
        })
      );
    }
    case 'CHANGE_ANSWER_MULTIPLE_CHOICE': {
      const { stepId, sectionId, answer } = action.payload;
      return updateSectionOfType(
        state,
        stepId,
        sectionId,
        'multiple-choice',
        (s) => ({
          ...s,
          didInteract: true,
          isAnswered: isMultipleChoiceQuestionAnswered({
            ...s,
            answer,
          }),
          answer,
        })
      );
    }
    case 'CHANGE_ANSWER_MATCHING': {
      const { stepId, sectionId, answer } = action.payload;
      return updateSectionOfType(state, stepId, sectionId, 'matching', (s) => ({
        ...s,
        didInteract: true,
        isAnswered: isMatchingQuestionAnswered({
          ...s,
          answer,
        }),
        answer,
      }));
    }
    case 'CHANGE_ANSWER_FILL_IN_THE_GAP': {
      const { stepId, sectionId, answer } = action.payload;
      return updateSectionOfType(
        state,
        stepId,
        sectionId,
        'fill-in-the-gap',
        (s) => ({
          ...s,
          didInteract: true,
          isAnswered: isFillInTheGapQuestionAnswered({
            ...s,
            answer,
          }),
          answer,
        })
      );
    }
    case 'CHANGE_ANSWER_TEXT_TEMPLATE': {
      const { stepId, sectionId, answer } = action.payload;
      return updateSectionOfType(
        state,
        stepId,
        sectionId,
        'text-template',
        (s) => ({
          ...s,
          didInteract: true,
          isAnswered: isTextTemplateQuestionAnswered({
            ...s,
            answer,
          }),
          answer,
        })
      );
    }
    case 'CHANGE_ANSWER_PY_PLAYGROUND': {
      const { stepId, sectionId, answer } = action.payload;
      return updateSectionOfType(
        state,
        stepId,
        sectionId,
        'py-playground',
        (s) => ({
          ...s,
          didInteract: true,
          isAnswered: isPythonPlaygroundQuestionAnswered({
            ...s,
            answer,
          }),
          answer,
        })
      );
    }
    case 'CHANGE_ANSWER_LLM_PLAYGROUND': {
      const { stepId, sectionId, answer } = action.payload;
      return updateSectionOfType(
        state,
        stepId,
        sectionId,
        'llm-playground',
        (s) => ({
          ...s,
          didInteract: true,
          isAnswered: isLLMPlaygroundQuestionAnswered({
            ...s,
            answer,
          }),
          answer,
        })
      );
    }
    case 'CHANGE_ANSWER_MATRIX_RANKING': {
      const { stepId, sectionId, answer } = action.payload;
      return updateSectionOfType(
        state,
        stepId,
        sectionId,
        'matrix-ranking',
        (s) => ({
          ...s,
          didInteract: true,
          isAnswered: isMatrixRankingQuestionAnswered({
            ...s,
            answer,
          }),
          answer,
        })
      );
    }
    case 'CHANGE_ANSWER_PY_EDITOR': {
      const { stepId, sectionId, answer } = action.payload;
      return updateSectionOfType(
        state,
        stepId,
        sectionId,
        'py-editor',
        (s) => ({
          ...s,
          didInteract: true,
          isAnswered: isPythonEditorQuestionAnswered({
            ...s,
            answer,
          }),
          answer,
        })
      );
    }
    case 'CHANGE_ANSWER_LLM_EDITOR': {
      const { stepId, sectionId, answer } = action.payload;
      return updateSectionOfType(
        state,
        stepId,
        sectionId,
        'llm-editor',
        (s) => ({
          ...s,
          didInteract: true,
          isAnswered: isLLMEditorQuestionAnswered({
            ...s,
            answer,
          }),
          answer,
        })
      );
    }
    case 'CHANGE_FOLLOW_UP_ANSWER': {
      const { stepId, sectionId, followUpAnswer } = action.payload;
      const section = state.steps
        .find((s) => s.id === stepId)
        ?.sections.find((s) => s.id === sectionId);
      const hasFollowUp = section?.hasFollowUp;
      if (!hasFollowUp) {
        throw new Error('Section does not have follow up');
      }
      return {
        ...state,
        steps: state.steps.map((step) => ({
          ...step,
          sections: step.sections.map((section) => {
            if (section.id === sectionId && section.hasFollowUp) {
              return {
                ...section,
                followUpAnswer,
                isAnswered: isSectionAnswered({
                  ...section,
                  followUpAnswer,
                }),
              };
            }
            return section;
          }),
        })),
      };
    }
  }
}
